import { useEffect, useState, type ReactNode } from 'react';
import { IconShield } from './icons';

export interface NavItem {
  id: string;
  label: string;
}

/** Scroll-spy: highlight the nav item whose section is nearest the top of the viewport. */
function useActiveSection(nav: NavItem[] | undefined): string | undefined {
  const [active, setActive] = useState<string | undefined>(nav?.[0]?.id);
  useEffect(() => {
    if (!nav || nav.length === 0) return;
    const els = nav
      .map((n) => document.getElementById(n.id))
      .filter((e): e is HTMLElement => e !== null);
    if (els.length === 0) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        const top = visible[0];
        if (top) setActive(top.target.id);
      },
      { rootMargin: '-45% 0px -50% 0px', threshold: 0 },
    );
    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [nav]);
  return active;
}

export function AppShell({
  tenantName,
  adminName,
  nav,
  onNavigate,
  children,
}: {
  tenantName?: string | null;
  adminName?: string | null;
  nav?: NavItem[];
  activeNav?: string;
  onNavigate?: (id: string) => void;
  children: ReactNode;
}) {
  const active = useActiveSection(nav);

  function go(id: string) {
    if (onNavigate) onNavigate(id);
    else document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  return (
    <div className="shell">
      <header className="shell__header">
        <div className="shell__brand">
          <span className="shell__logo" aria-hidden>
            <IconShield />
          </span>
          <span className="shell__title">x365</span>
        </div>

        <div className="shell__ident">
          {tenantName && <span className="shell__tenant">{tenantName}</span>}
          {adminName && <span className="shell__admin">{adminName}</span>}
          <span className="shell__moat" title="No tenant data leaves your browser.">
            zero-egress
          </span>
        </div>
      </header>

      <div className="shell__body">
        {nav && nav.length > 0 && (
          <nav className="shell__sidebar" aria-label="Sections">
            <div className="snav">
              <p className="snav__label">Sections</p>
              {nav.map((item) => (
                <button
                  key={item.id}
                  className={`snav__item${active === item.id ? ' is-active' : ''}`}
                  onClick={() => go(item.id)}
                  aria-current={active === item.id ? 'true' : undefined}
                >
                  <span className="snav__dot" aria-hidden />
                  {item.label}
                </button>
              ))}
            </div>
          </nav>
        )}

        <main className="shell__main">{children}</main>
      </div>

      <footer className="shell__footer">
        Delegated, client-side only. No backend, no telemetry, no third-party calls.
      </footer>
    </div>
  );
}
